import React, { useEffect, useState } from 'react';
import {
  Download as DownloadIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { format } from 'date-fns/format';

import { useGetStripeInvoicesQuery } from '../store';
import { useStripeConnect } from '../store/hooks';
import type { StripeInvoice } from '../types';

const ROWS_PER_PAGE = 10;

interface InvoiceStatusChipProps {
  status: string;
}

const InvoiceStatusChip: React.FC<InvoiceStatusChipProps> = ({ status }) => {
  const getStatusConfig = () => {
    switch (status.toLowerCase()) {
      case 'paid':
        return { label: 'Paid', color: 'success' as const };
      case 'open':
        return { label: 'Open', color: 'warning' as const };
      case 'void':
        return { label: 'Void', color: 'default' as const };
      case 'uncollectible':
        return { label: 'Uncollectible', color: 'error' as const };
      case 'draft':
        return { label: 'Draft', color: 'info' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  const { label, color } = getStatusConfig();
  return <Chip label={label} color={color} size="small" />;
};

interface PaginationState {
  cursors: string[];
  currentPage: number;
}

export const StripeInvoicesTable: React.FC = () => {
  const { hasStripeAccount } = useStripeConnect();
  const { payment } = useEventTracking();
  const [pagination, setPagination] = useState<PaginationState>({
    cursors: [],
    currentPage: 0,
  });

  // Determine the starting_after cursor for the current page
  const startingAfter =
    pagination.currentPage > 0 ? pagination.cursors[pagination.currentPage - 1] : undefined;

  const { data, isLoading, isFetching, error } = useGetStripeInvoicesQuery(
    { limit: ROWS_PER_PAGE, startingAfter },
    { skip: !hasStripeAccount },
  );

  // Update cursors when new data arrives
  useEffect(() => {
    const nextCursor = data?.next_cursor;
    if (nextCursor && !pagination.cursors.includes(nextCursor)) {
      setPagination(prev => ({
        ...prev,
        cursors: [...prev.cursors.slice(0, prev.currentPage), nextCursor],
      }));
    }
  }, [data?.next_cursor, pagination.currentPage, pagination.cursors]);

  // Track invoice table view when data loads
  useEffect(() => {
    if (data?.invoices && data.invoices.length > 0) {
      payment.invoicesViewed({});
    }
  }, [data?.invoices, payment]);

  // Don't render if Stripe is not connected
  if (!hasStripeAccount) {
    return null;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load invoices. Please try again later.</Typography>
      </Box>
    );
  }

  const invoices = data?.invoices || [];
  const hasMore = data?.has_more || false;
  const isFirstPage = pagination.currentPage === 0;
  const isLastPage = !hasMore;

  const handleNextPage = () => {
    if (hasMore) {
      setPagination(prev => ({
        ...prev,
        currentPage: prev.currentPage + 1,
      }));
    }
  };

  const handlePreviousPage = () => {
    if (!isFirstPage) {
      setPagination(prev => ({
        ...prev,
        currentPage: Math.max(0, prev.currentPage - 1),
      }));
    }
  };

  const formatCurrency = (amountInCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amountInCents / 100);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (isLoading && !isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {invoices.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No invoices yet. Invoices will appear here once you start processing payments.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice: StripeInvoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {invoice.number || invoice.id.slice(-8)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(invoice.created)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          {invoice.customer_name || 'Unknown'}
                        </Typography>
                        {invoice.customer_email && (
                          <Typography variant="caption" color="text.secondary">
                            {invoice.customer_email}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusChip status={invoice.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(invoice.amount_due)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={invoice.amount_paid > 0 ? 'success.main' : 'text.secondary'}
                      >
                        {formatCurrency(invoice.amount_paid)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {invoice.hosted_invoice_url && (
                          <Tooltip title="View invoice">
                            <IconButton
                              size="small"
                              component={Link}
                              href={invoice.hosted_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {invoice.pdf && (
                          <Tooltip title="Download PDF">
                            <IconButton
                              size="small"
                              component={Link}
                              href={invoice.pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Custom cursor-based pagination controls */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={2}>
            <Typography variant="body2" color="text.secondary">
              Page {pagination.currentPage + 1}
              {isFetching && ' (Loading...)'}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<NavigateBeforeIcon />}
                onClick={handlePreviousPage}
                disabled={isFirstPage || isFetching}
              >
                Previous
              </Button>
              <Button
                size="small"
                endIcon={<NavigateNextIcon />}
                onClick={handleNextPage}
                disabled={isLastPage || isFetching}
              >
                Next
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
};
